import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtNum } from "@/lib/api";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Loader2, HardDrive, DownloadCloud, RefreshCcw, Play, Trash2, Save,
  ShieldAlert, Cloud, CloudOff, PlugZap, FileArchive, Upload as UploadIcon,
  History, Clock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (n) => {
  if (n == null) return "–";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtDatum = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
};

const StatusBadge = ({ status }) => {
  const map = {
    ok: { label: "OK", variant: "default" },
    fehler: { label: "Fehler", variant: "destructive" },
    aus: { label: "nur lokal", variant: "outline" },
    import: { label: "importiert", variant: "secondary" },
  };
  const cfg = map[status] || map.aus;
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

// ------------------ Restore-Dialog ------------------
const RestoreDialog = ({ backup, bereiche, onClose, onDone }) => {
  const [checkedBereiche, setCheckedBereiche] = useState({});
  const [checkedCollections, setCheckedCollections] = useState({});
  const [modus, setModus] = useState("replace");
  const [passphrase, setPassphrase] = useState("");
  const [bestaetigung, setBestaetigung] = useState("");
  const [running, setRunning] = useState(false);
  const [forceAbrechnungen, setForceAbrechnungen] = useState(false);
  const [vorschau, setVorschau] = useState(null);
  const [vorschauLoading, setVorschauLoading] = useState(false);

  const inBackup = new Set((backup?.collections || []).map((c) => c.name));

  const toggleBereich = (key) => {
    const b = bereiche[key];
    const jetzt = !checkedBereiche[key];
    setCheckedBereiche((s) => ({ ...s, [key]: jetzt }));
    setCheckedCollections((s) => {
      const next = { ...s };
      for (const c of b.collections) {
        if (inBackup.has(c)) next[c] = jetzt;
      }
      return next;
    });
    setVorschau(null);
  };

  const selected = Object.keys(checkedCollections).filter((k) => checkedCollections[k]);

  // Presets Alle / Keine
  const selectAll = () => {
    const nextBereiche = {};
    const nextCollections = {};
    for (const [key, b] of Object.entries(bereiche)) {
      nextBereiche[key] = true;
      for (const c of b.collections) if (inBackup.has(c)) nextCollections[c] = true;
    }
    setCheckedBereiche(nextBereiche);
    setCheckedCollections(nextCollections);
    setVorschau(null);
  };
  const selectNone = () => {
    setCheckedBereiche({});
    setCheckedCollections({});
    setVorschau(null);
  };

  const loadVorschau = async () => {
    if (selected.length === 0) {
      toast.error("Bitte mindestens eine Collection auswählen.");
      return;
    }
    setVorschauLoading(true);
    try {
      const { data } = await api.post(`/backup/${backup.id}/vorschau`, {
        collections: selected, modus, passphrase: passphrase || null,
      });
      setVorschau(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setVorschauLoading(false); }
  };

  const brauchtForce = vorschau?.warnungen?.some((w) => w.stufe === "kritisch");

  const restore = async () => {
    if (bestaetigung !== "ICH BIN MIR SICHER") {
      toast.error("Bitte tippen Sie 'ICH BIN MIR SICHER' zur Bestätigung.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Bitte mindestens eine Collection auswählen.");
      return;
    }
    if (brauchtForce && !forceAbrechnungen) {
      toast.error("Bitte Force-Checkbox aktivieren, um freigegebene/stornierte Abrechnungen zu überschreiben.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/backup/${backup.id}/restore`, {
        collections: selected, modus, passphrase: passphrase || null,
        bestaetigung,
        force_freigegebene_abrechnungen: forceAbrechnungen,
      });
      toast.success(`Restore fertig: ${data.anzahl_dokumente} Dokumente in ${data.restauriert.length} Collection(s).`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            Restore aus Backup
          </DialogTitle>
          <DialogDescription>
            Backup vom {fmtDatum(backup?.zeitpunkt)} · {backup?.anzahl_collections} Collections · {backup?.anzahl_dokumente} Dokumente
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border-2 border-destructive/40 bg-destructive/5 p-3 text-sm space-y-1">
          <p className="font-medium text-destructive">Warnung – dieser Vorgang ist destruktiv:</p>
          <ul className="list-disc pl-5 text-xs">
            <li>Bei Modus <strong>„Ersetzen“</strong> wird die ausgewählte Collection komplett geleert und neu befüllt.</li>
            <li>Bei Modus <strong>„Zusammenführen“</strong> werden Dokumente mit gleicher ID überschrieben, neue hinzugefügt.</li>
            <li>Vorhandene Referenzen (z. B. auf Preisrevisionen) können ungültig werden, wenn Sie nur Teilbereiche restaurieren.</li>
          </ul>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">1. Bereiche und Collections auswählen</Label>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline"
                  onClick={selectAll} data-testid="restore-select-all">
                  Alle auswählen
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={selectNone} data-testid="restore-select-none">
                  Keine
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {Object.entries(bereiche).map(([key, b]) => (
                <div key={key} className="rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={!!checkedBereiche[key]}
                      onCheckedChange={() => toggleBereich(key)}
                      data-testid={`bereich-${key}`}
                    />
                    <span className="font-medium text-sm">{b.label}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 pl-6">
                    {b.collections.map((c) => {
                      const vorhanden = inBackup.has(c);
                      return (
                        <label key={c}
                          className={`flex items-center gap-2 text-xs ${vorhanden ? "" : "opacity-50"}`}
                          title={vorhanden ? "" : "Nicht im Backup enthalten"}>
                          <Checkbox
                            checked={!!checkedCollections[c] && vorhanden}
                            disabled={!vorhanden}
                            onCheckedChange={(v) => {
                              setCheckedCollections((s) => ({ ...s, [c]: !!v }));
                              setVorschau(null);
                            }} />
                          <span className="font-mono">{c}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">2. Modus</Label>
              <Select value={modus} onValueChange={(v) => { setModus(v); setVorschau(null); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="replace">Ersetzen (drop + insert)</SelectItem>
                  <SelectItem value="merge">Zusammenführen (upsert per ID)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {backup?.verschluesselt && (
              <div className="space-y-1.5">
                <Label className="text-sm">Passphrase (verschlüsseltes Backup)</Label>
                <Input type="password" value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">3. Vorschau prüfen (optional, empfohlen)</Label>
              <Button size="sm" variant="secondary"
                onClick={loadVorschau} disabled={vorschauLoading || selected.length === 0}
                data-testid="restore-vorschau-load">
                {vorschauLoading
                  ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  : <Clock className="h-3.5 w-3.5 mr-2" />}
                Vorschau laden
              </Button>
            </div>
            {vorschau && (
              <div className="rounded-md border bg-secondary/30 p-3 space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="text-left px-2 py-1">Collection</th>
                        <th className="text-left px-2 py-1">Aktion</th>
                        <th className="text-right px-2 py-1">Aktuell</th>
                        <th className="text-right px-2 py-1">Aus Backup</th>
                        <th className="text-right px-2 py-1">Δ (Netto)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vorschau.collections.map((r) => (
                        <tr key={r.collection} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.collection}</td>
                          <td className="px-2 py-1">{r.aktion}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmtNum(r.aktuell)}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmtNum(r.aus_backup)}</td>
                          <td className="px-2 py-1 text-right font-mono">
                            {r.netto_delta == null ? "–"
                              : (r.netto_delta > 0 ? "+" : "") + fmtNum(r.netto_delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(vorschau.warnungen || []).map((w, idx) => (
                  <div key={idx}
                    className={`rounded p-2 text-xs flex items-start gap-2 ${
                      w.stufe === "kritisch"
                        ? "border border-destructive/40 bg-destructive/10 text-destructive"
                        : "border border-amber-300 bg-amber-50 text-amber-900"
                    }`}>
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium capitalize">{w.stufe} · {w.collection}</p>
                      <p>{w.text}</p>
                    </div>
                  </div>
                ))}
                {brauchtForce && (
                  <label className="flex items-start gap-2 text-xs rounded border border-destructive/40 bg-destructive/5 p-2"
                         data-testid="restore-force-label">
                    <Checkbox checked={forceAbrechnungen}
                      onCheckedChange={(v) => setForceAbrechnungen(!!v)}
                      data-testid="restore-force-checkbox" />
                    <span>
                      <strong>Auch freigegebene/stornierte Abrechnungen überschreiben.</strong>{" "}
                      Nur aktivieren, wenn die rechtliche Rücknahme abgestimmt ist
                      und ein ERP-Storno vorliegt.
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">4. Bestätigung</Label>
            <Input value={bestaetigung}
              onChange={(e) => setBestaetigung(e.target.value)}
              placeholder="Bitte tippen: ICH BIN MIR SICHER"
              data-testid="restore-bestaetigung" />
            <p className="text-[11px] text-muted-foreground">
              Auswahl: {selected.length} Collection(s)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button variant="destructive" onClick={restore}
            disabled={running || bestaetigung !== "ICH BIN MIR SICHER" || selected.length === 0}
            data-testid="restore-execute">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
            Restore ausführen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------ Zeitpunkt-Restore (SCD Type 2) ------------------
const fmtCount = (n) => (typeof n === "number" ? n.toLocaleString("de-DE") : "0");

const ZeitpunktRestoreCard = () => {
  // Standard: heute 00:00 → als YYYY-MM-DDTHH:mm
  const heuteInput = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
  }, []);
  const [zeitpunkt, setZeitpunkt] = useState(heuteInput);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [inklLieferanten, setInklLieferanten] = useState(true);
  const [inklPreismodell, setInklPreismodell] = useState(true);
  const [inklAbrechnungen, setInklAbrechnungen] = useState(false);
  const [forceAbrechnungen, setForceAbrechnungen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState("");
  const [ergebnis, setErgebnis] = useState(null);

  const loadPreview = async () => {
    if (!zeitpunkt) {
      toast.error("Bitte Zeitpunkt wählen.");
      return;
    }
    setLoading(true);
    setErgebnis(null);
    try {
      const { data } = await api.post("/backup/zeitpunkt-preview", {
        zeitpunkt: zeitpunkt.length === 16 ? zeitpunkt + ":00" : zeitpunkt,
      });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const restore = async () => {
    if (bestaetigung !== "ICH BIN MIR SICHER") {
      toast.error("Bitte tippen Sie 'ICH BIN MIR SICHER' zur Bestätigung.");
      return;
    }
    if (!inklLieferanten && !inklPreismodell && !inklAbrechnungen) {
      toast.error("Bitte mindestens einen Bereich auswählen.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/backup/zeitpunkt-restore", {
        zeitpunkt: zeitpunkt.length === 16 ? zeitpunkt + ":00" : zeitpunkt,
        include_lieferanten: inklLieferanten,
        include_preismodell: inklPreismodell,
        include_abrechnungen: inklAbrechnungen,
        force_freigegebene_abrechnungen: forceAbrechnungen,
        bestaetigung,
      });
      setErgebnis(data);
      setBestaetigung("");
      setForceAbrechnungen(false);
      setPreview(null);
      toast.success("Zeitpunkt-Rollback ausgeführt. Sicherheits-Backup wurde erstellt.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  const summeAktionen = preview
    ? (inklLieferanten
        ? (preview.lieferanten_versionen?.loeschen || 0)
          + (preview.lieferanten_versionen?.wiedereroeffnen || 0)
        : 0)
      + (inklPreismodell
        ? (preview.preismodell_revisionen?.loeschen || 0)
          + (preview.preismodell_revisionen?.wiedereroeffnen || 0)
        : 0)
      + (inklAbrechnungen
        ? (preview.abrechnungen?.erstellt_nach || 0)
          + (preview.abrechnungen?.freigegeben_nach || 0)
          + (preview.abrechnungen?.storniert_nach || 0)
        : 0)
    : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-5 w-5 text-primary" /> Zeitpunkt-Wiederherstellung (SCD Type 2)
        </CardTitle>
        <CardDescription>
          Rollt <strong>historisierte</strong> Daten (Lieferantenstammdaten, Preismodell-Revisionen)
          und optional Abrechnungen auf einen Zeitpunkt zurück. Es wird
          automatisch vorher ein Sicherheits-Backup erstellt.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> Hinweise zur Zeitpunkt-Wiederherstellung
          </p>
          <ul className="list-disc pl-5">
            <li>Wirkt nur auf historisierte Datensätze (SCD Type 2).</li>
            <li>Neu angelegte Versionen <em>nach</em> dem Zeitpunkt werden entfernt.</li>
            <li>Geschlossene Versionen (mit Stichtag nach dem Zeitpunkt) werden wieder eröffnet.</li>
            <li>Nicht-historisierte Daten (Milchsorten, Gruppen, Touren) müssen ggf. per klassischem Restore aus einem Backup hergestellt werden.</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Zeitpunkt (Zielzustand)</Label>
            <Input
              type="datetime-local"
              value={zeitpunkt}
              onChange={(e) => { setZeitpunkt(e.target.value); setPreview(null); }}
              data-testid="zeitpunkt-restore-datetime"
            />
            <p className="text-[11px] text-muted-foreground">
              Der Zustand vor diesem Zeitpunkt wird wiederhergestellt.
            </p>
          </div>
          <div className="flex items-end">
            <Button onClick={loadPreview} disabled={loading} data-testid="zeitpunkt-preview-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Clock className="h-4 w-4 mr-2" />}
              Preview laden
            </Button>
          </div>
        </div>

        {preview && (
          <div className="rounded-lg border bg-card p-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Zustand für Zeitpunkt <strong>{new Date(preview.zeitpunkt).toLocaleString("de-DE")}</strong>:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className={`rounded-md border p-3 space-y-1 ${inklLieferanten ? "" : "opacity-50"}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Lieferantenversionen</Label>
                  <Checkbox
                    checked={inklLieferanten}
                    onCheckedChange={setInklLieferanten}
                    data-testid="zeitpunkt-incl-lieferanten"
                  />
                </div>
                <p className="text-xs">
                  <span className="font-mono">{fmtCount(preview.lieferanten_versionen?.loeschen)}</span> löschen ·{" "}
                  <span className="font-mono">{fmtCount(preview.lieferanten_versionen?.wiedereroeffnen)}</span> wieder öffnen
                </p>
              </div>
              <div className={`rounded-md border p-3 space-y-1 ${inklPreismodell ? "" : "opacity-50"}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Preismodell-Revisionen</Label>
                  <Checkbox
                    checked={inklPreismodell}
                    onCheckedChange={setInklPreismodell}
                    data-testid="zeitpunkt-incl-preismodell"
                  />
                </div>
                <p className="text-xs">
                  <span className="font-mono">{fmtCount(preview.preismodell_revisionen?.loeschen)}</span> löschen ·{" "}
                  <span className="font-mono">{fmtCount(preview.preismodell_revisionen?.wiedereroeffnen)}</span> wieder öffnen
                </p>
              </div>
              <div className={`rounded-md border p-3 space-y-1 ${inklAbrechnungen ? "" : "opacity-50"}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Abrechnungen (optional)</Label>
                  <Checkbox
                    checked={inklAbrechnungen}
                    onCheckedChange={setInklAbrechnungen}
                    data-testid="zeitpunkt-incl-abrechnungen"
                  />
                </div>
                <p className="text-xs">
                  <span className="font-mono">{fmtCount(preview.abrechnungen?.erstellt_nach)}</span> löschen ·{" "}
                  <span className="font-mono">{fmtCount(preview.abrechnungen?.freigegeben_nach)}</span> Freigaben ·{" "}
                  <span className="font-mono">{fmtCount(preview.abrechnungen?.storniert_nach)}</span> Stornos zurücksetzen
                </p>
              </div>
            </div>

            <div className="rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">
              <strong>Audit-Log:</strong> {fmtCount(preview.audit_log_nach_zeitpunkt)} Einträge nach diesem Zeitpunkt. Diese bleiben zur Nachvollziehbarkeit erhalten.
            </div>

            {(preview.warnungen || []).map((w, idx) => (
              <div key={idx}
                data-testid="zeitpunkt-warnung"
                className={`rounded p-2 text-xs flex items-start gap-2 ${
                  w.stufe === "kritisch"
                    ? "border-2 border-destructive/40 bg-destructive/10 text-destructive"
                    : "border border-amber-300 bg-amber-50 text-amber-900"
                }`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold capitalize">{w.stufe} · {w.bereich}</p>
                  <p>{w.text}</p>
                </div>
              </div>
            ))}

            {inklAbrechnungen && preview.kritisch && (
              <label className="flex items-start gap-2 text-xs rounded border-2 border-destructive/40 bg-destructive/5 p-2"
                     data-testid="zeitpunkt-force-label">
                <Checkbox checked={forceAbrechnungen}
                  onCheckedChange={(v) => setForceAbrechnungen(!!v)}
                  data-testid="zeitpunkt-force-checkbox" />
                <span>
                  <strong>Auch freigegebene/stornierte Abrechnungen zurücksetzen.</strong>{" "}
                  Diese sind rechtsverbindlich. Nur aktivieren, wenn die Rücknahme
                  vorher fachlich abgestimmt wurde und ein ERP-Storno vorliegt.
                </span>
              </label>
            )}

            <Separator />

            <div className="space-y-1.5">
              <Label className="text-sm">Bestätigung</Label>
              <Input
                value={bestaetigung}
                onChange={(e) => setBestaetigung(e.target.value)}
                placeholder="Bitte tippen: ICH BIN MIR SICHER"
                data-testid="zeitpunkt-bestaetigung"
              />
              <p className="text-[11px] text-muted-foreground">
                Betroffen: <span className="font-mono">{fmtCount(summeAktionen)}</span> Datensätze insgesamt.
              </p>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => setPreview(null)}>Abbrechen</Button>
              <Button
                variant="destructive"
                onClick={restore}
                disabled={running || bestaetigung !== "ICH BIN MIR SICHER" || summeAktionen === 0 || (inklAbrechnungen && preview.kritisch && !forceAbrechnungen)}
                data-testid="zeitpunkt-restore-execute"
              >
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <History className="h-4 w-4 mr-2" />}
                Zeitpunkt-Rollback ausführen
              </Button>
            </div>
          </div>
        )}

        {ergebnis && (
          <div className="rounded-lg border-2 border-primary/30 bg-primary/[0.04] p-4 space-y-2">
            <p className="text-sm font-semibold">Rollback erfolgreich ausgeführt</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-muted-foreground">Sicherheits-Backup</p>
                <p className="font-mono">{ergebnis.sicherheits_backup_id || "–"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Lieferanten gelöscht / re-open</p>
                <p className="font-mono">
                  {fmtCount(ergebnis.lieferanten_versionen_geloescht)} · {fmtCount(ergebnis.lieferanten_versionen_wiedereroeffnet)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Preismodell gelöscht / re-open</p>
                <p className="font-mono">
                  {fmtCount(ergebnis.preismodell_revisionen_geloescht)} · {fmtCount(ergebnis.preismodell_revisionen_wiedereroeffnet)}
                </p>
              </div>
              {typeof ergebnis.abrechnungen_geloescht === "number" && (
                <div>
                  <p className="text-muted-foreground">Abrechnungen gelöscht</p>
                  <p className="font-mono">{fmtCount(ergebnis.abrechnungen_geloescht)}</p>
                </div>
              )}
            </div>
            <details className="text-xs mt-2">
              <summary className="cursor-pointer text-muted-foreground">Alle Aktionen anzeigen</summary>
              <ul className="mt-2 list-disc pl-5 space-y-0.5">
                {(ergebnis.aktionen || []).map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ------------------ Konfigurations-Card ------------------
const KonfigCard = ({ konfig, onSaved }) => {
  const [form, setForm] = useState(konfig);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { setForm(konfig); }, [konfig]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/backup/konfiguration", form);
      toast.success("Backup-Konfiguration gespeichert.");
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const testen = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/backup/konfiguration/test", form);
      if (data.erfolg) toast.success(data.meldung);
      else toast.error(data.meldung);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTesting(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Automatischer Backup-Zeitplan</CardTitle>
        <CardDescription>
          Backups werden lokal unter <code className="font-mono text-[11px]">/app/backend/backups</code>
          {" "}abgelegt und optional per FTP/SFTP repliziert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Zeitplan aktiv</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch checked={form.aktiv}
                onCheckedChange={(v) => setF("aktiv", v)} data-testid="backup-aktiv" />
              <span className="text-sm">{form.aktiv ? "Aktiv" : "Deaktiviert"}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Modus</Label>
            <Select value={form.modus} onValueChange={(v) => setF("modus", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="taeglich">Täglich zu fester Uhrzeit</SelectItem>
                <SelectItem value="intervall">Intervall (Stunden)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.modus === "taeglich" ? (
            <div className="space-y-1.5">
              <Label className="text-sm">Uhrzeit</Label>
              <Input type="time" value={form.uhrzeit || "02:30"}
                onChange={(e) => setF("uhrzeit", e.target.value)} />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-sm">Intervall (Stunden)</Label>
              <Input type="number" min="1" max="168" value={form.intervall_stunden}
                onChange={(e) => setF("intervall_stunden", Number(e.target.value))} />
            </div>
          )}
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Retention-Strategie</p>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={form.retention_modus === "flat" ? "default" : "outline"}
                onClick={() => setF("retention_modus", "flat")}
                data-testid="retention-mode-flat"
              >
                Einfach
              </Button>
              <Button
                size="sm"
                variant={form.retention_modus === "gfs" ? "default" : "outline"}
                onClick={() => setF("retention_modus", "gfs")}
                data-testid="retention-mode-gfs"
                title="Grandfather-Father-Son: kombiniert kurzfristige Snapshots mit monatlichen/jährlichen Archiven"
              >
                GFS (empfohlen)
              </Button>
            </div>
          </div>

          {form.retention_modus === "flat" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Lokal – neueste X Backups</Label>
                <Input type="number" min="1" max="365" value={form.retention_lokal}
                  onChange={(e) => setF("retention_lokal", Number(e.target.value))}
                  data-testid="retention-lokal-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Remote – neueste X Backups</Label>
                <Input type="number" min="1" max="365" value={form.retention_remote}
                  onChange={(e) => setF("retention_remote", Number(e.target.value))}
                  data-testid="retention-remote-input" />
              </div>
              <p className="col-span-full text-[11px] text-muted-foreground">
                Einfache Rotation: nach X neuen Backups wird das älteste gelöscht. Nach {form.retention_lokal || 30} Tagen sind keine älteren Snapshots mehr verfügbar.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Töchter (Tage)</Label>
                <Input type="number" min="1" max="90" value={form.retention_taeglich ?? 14}
                  onChange={(e) => setF("retention_taeglich", Number(e.target.value))}
                  data-testid="retention-taeglich-input" />
                <p className="text-[10px] text-muted-foreground">Neueste X Backups behalten</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Väter (Monate)</Label>
                <Input type="number" min="0" max="60" value={form.retention_monatlich ?? 12}
                  onChange={(e) => setF("retention_monatlich", Number(e.target.value))}
                  data-testid="retention-monatlich-input" />
                <p className="text-[10px] text-muted-foreground">Je 1 Backup/Monat, X Monate rückwärts</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Großväter (Jahre)</Label>
                <Input type="number" min="0" max="20" value={form.retention_jaehrlich ?? 7}
                  onChange={(e) => setF("retention_jaehrlich", Number(e.target.value))}
                  data-testid="retention-jaehrlich-input" />
                <p className="text-[10px] text-muted-foreground">Je 1 Backup/Jahr, X Jahre rückwärts (DSGVO 7 J)</p>
              </div>
              <div className="col-span-full space-y-1.5">
                <Label className="text-sm">Remote – neueste X Backups</Label>
                <Input type="number" min="1" max="365" value={form.retention_remote}
                  onChange={(e) => setF("retention_remote", Number(e.target.value))}
                  data-testid="retention-remote-input" />
              </div>
              <p className="col-span-full text-[11px] text-muted-foreground">
                Grandfather-Father-Son: bei täglichen Backups werden aktuell ca.{" "}
                <span className="font-num">
                  {(form.retention_taeglich ?? 14) + (form.retention_monatlich ?? 12) + (form.retention_jaehrlich ?? 7)}
                </span>
                {" "}Snapshots aufbewahrt – kurzfristige Wiederherstellungen bleiben schnell, jahrzehntelange Historie mit geringem Platzbedarf.
              </p>
            </div>
          )}
        </div>

        <Separator />

        <div>
          <p className="text-sm font-medium mb-2">Verschlüsselung</p>
          <div className="flex items-center gap-2">
            <Switch checked={form.verschluesselung}
              onCheckedChange={(v) => setF("verschluesselung", v)} />
            <span className="text-sm">
              {form.verschluesselung
                ? "AES-256 – Passphrase beim Backup erforderlich"
                : "Unverschlüsselt (Standard, ermoglicht Einsicht/Validierung)"}
            </span>
          </div>
        </div>

        <Separator />

        <div>
          <p className="text-sm font-medium mb-2">Remote-Replikation</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Ziel-Typ</Label>
              <Select value={form.remote_typ} onValueChange={(v) => setF("remote_typ", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aus">Aus (nur lokal)</SelectItem>
                  <SelectItem value="sftp">SFTP</SelectItem>
                  <SelectItem value="ftp">FTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.remote_typ !== "aus" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-sm">Host</Label>
                  <Input value={form.remote_host || ""}
                    onChange={(e) => setF("remote_host", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Port</Label>
                  <Input type="number" value={form.remote_port || (form.remote_typ === "sftp" ? 22 : 21)}
                    onChange={(e) => setF("remote_port", Number(e.target.value))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Verzeichnis</Label>
                  <Input value={form.remote_verzeichnis || "/backups"}
                    onChange={(e) => setF("remote_verzeichnis", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Benutzer</Label>
                  <Input value={form.remote_user || ""}
                    onChange={(e) => setF("remote_user", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Passwort</Label>
                  <Input type="password" value={form.remote_passwort || ""}
                    placeholder={form.remote_passwort === "***" ? "unverändert" : ""}
                    onChange={(e) => setF("remote_passwort", e.target.value)} />
                </div>
                {form.remote_typ === "ftp" && (
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <Switch checked={form.remote_tls}
                      onCheckedChange={(v) => setF("remote_tls", v)} />
                    <span className="text-sm">FTPS (TLS)</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save} disabled={saving} data-testid="backup-konfig-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Konfiguration speichern
          </Button>
          {form.remote_typ !== "aus" && (
            <Button variant="outline" onClick={testen} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
              Verbindung testen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ------------------ Hauptseite ------------------
export default function AdminBackupPage() {
  const [uebersicht, setUebersicht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/uebersicht");
      setUebersicht(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const backupErstellen = async () => {
    setCreating(true);
    try {
      await api.post("/backup/erstellen", {});
      toast.success("Backup erstellt.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreating(false); }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/backup/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${b.id}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadZuRemote = async (b) => {
    try {
      const { data } = await api.post(`/backup/${b.id}/upload`);
      toast.success(data.meldung);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loeschen = async (b) => {
    if (!window.confirm(`Backup vom ${fmtDatum(b.zeitpunkt)} löschen?`)) return;
    try {
      await api.delete(`/backup/${b.id}`);
      toast.success("Backup gelöscht.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadArchiv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/backup/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Backup importiert: ${data.id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) {
    return <div className="flex justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const backups = uebersicht?.backups || [];
  const konfig = uebersicht?.konfiguration || {};
  const bereiche = uebersicht?.bereiche || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Backup & Restore</h1>
          <p className="text-sm text-muted-foreground">
            JSON-Snapshots aller Collections · lokal + optional per SFTP/FTP repliziert.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={backupErstellen} disabled={creating}
            data-testid="backup-create-now">
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HardDrive className="h-4 w-4 mr-2" />}
            Jetzt sichern
          </Button>
          <label>
            <input type="file" accept=".tar.gz,.tgz" className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) uploadArchiv(e.target.files[0]); }} />
            <Button variant="outline" asChild>
              <span><UploadIcon className="h-4 w-4 mr-2" /> Archiv importieren</span>
            </Button>
          </label>
        </div>
      </div>

      <Tabs defaultValue="backups">
        <TabsList>
          <TabsTrigger value="backups">Backups ({backups.length})</TabsTrigger>
          <TabsTrigger value="zeitpunkt" data-testid="tab-zeitpunkt">Zeitpunkt-Wiederherstellung</TabsTrigger>
          <TabsTrigger value="konfig">Konfiguration</TabsTrigger>
        </TabsList>

        <TabsContent value="backups" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>Modus</TableHead>
                      <TableHead className="text-right">Collections</TableHead>
                      <TableHead className="text-right">Dokumente</TableHead>
                      <TableHead className="text-right">Größe</TableHead>
                      <TableHead>Verschlüsselt</TableHead>
                      <TableHead>Remote</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          Noch keine Backups vorhanden. Klicken Sie auf „Jetzt sichern“.
                        </TableCell>
                      </TableRow>
                    )}
                    {backups.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>
                          <div className="font-num text-xs">{fmtDatum(b.zeitpunkt)}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{b.id}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={b.modus === "scheduled" ? "secondary" : "outline"}>
                            {b.modus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-num">{b.anzahl_collections}</TableCell>
                        <TableCell className="text-right font-num">{fmtNum(b.anzahl_dokumente, 0)}</TableCell>
                        <TableCell className="text-right font-num">{fmtBytes(b.groesse_bytes)}</TableCell>
                        <TableCell>{b.verschluesselt ? <Badge>AES-256</Badge> : <Badge variant="outline">nein</Badge>}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <StatusBadge status={b.remote_status || "aus"} />
                            {b.remote_meldung && <span className="text-[10px] text-muted-foreground truncate max-w-[150px]">{b.remote_meldung}</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end flex-wrap">
                            <Button size="sm" variant="ghost" onClick={() => download(b)}
                              title="Als .tar.gz herunterladen" data-testid={`bk-dl-${b.id}`}>
                              <DownloadCloud className="h-4 w-4" />
                            </Button>
                            {konfig.remote_typ !== "aus" && (
                              <Button size="sm" variant="ghost" onClick={() => uploadZuRemote(b)}
                                title="Zum Remote-Ziel senden">
                                <Cloud className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setRestoreBackup(b)}
                              data-testid={`bk-restore-${b.id}`}>
                              <RefreshCcw className="h-4 w-4 mr-1" /> Restore
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => loeschen(b)}
                              title="Löschen">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="zeitpunkt" className="mt-4">
          <ZeitpunktRestoreCard />
        </TabsContent>

        <TabsContent value="konfig" className="mt-4">
          <KonfigCard konfig={konfig} onSaved={load} />
        </TabsContent>
      </Tabs>

      {restoreBackup && (
        <RestoreDialog
          backup={restoreBackup}
          bereiche={bereiche}
          onClose={() => setRestoreBackup(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
